/**
 * Team Template Selector Component
 *
 * Allows users to browse and select from pre-configured team templates
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import {
  TEAM_TEMPLATES,
  getTemplateCategories,
  getTemplatesByCategory,
  type TeamTemplate,
} from '@/constants/teamTemplates'
import {
  SparklesIcon,
  ArrowRightIcon,
  UsersIcon,
  CheckIcon,
} from '@heroicons/react/24/outline'

interface TeamTemplateSelectorProps {
  onSelectTemplate?: (template: TeamTemplate) => void
  onClose?: () => void
}

export default function TeamTemplateSelector({
  onSelectTemplate,
  onClose,
}: TeamTemplateSelectorProps) {
  const navigate = useNavigate()
  const [selectedCategory, setSelectedCategory] = useState<TeamTemplate['category'] | 'all'>('all')
  const [selectedTemplate, setSelectedTemplate] = useState<TeamTemplate | null>(null)

  const categories = getTemplateCategories()

  const filteredTemplates =
    selectedCategory === 'all'
      ? TEAM_TEMPLATES
      : getTemplatesByCategory(selectedCategory)

  const handleUseTemplate = (template: TeamTemplate) => {
    if (onSelectTemplate) {
      onSelectTemplate(template)
    } else {
      // Navigate to team editor with template data
      navigate('/teams/new', { state: { template } })
    }
  }

  const getCategoryBadgeVariant = (category: string) => {
    const variants: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'gray'> = {
      consulting: 'primary',
      technical: 'success',
      creative: 'warning',
      analysis: 'error',
      general: 'gray',
    }
    return variants[category] || 'gray'
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <SparklesIcon className="w-8 h-8 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">Team Templates</h2>
          </div>
          <p className="text-gray-600 mt-1">
            Start with a pre-configured team template and customize it to your needs
          </p>
        </div>
        {onClose && (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      {/* Category Filter */}
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              selectedCategory === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Templates ({TEAM_TEMPLATES.length})
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                selectedCategory === category.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.label} ({category.count})
            </button>
          ))}
        </div>
      </Card>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => (
          <div key={template.id} onClick={() => setSelectedTemplate(template)} className="cursor-pointer">
            <Card
              className={`hover:shadow-lg transition-all ${
                selectedTemplate?.id === template.id
                  ? 'ring-2 ring-primary-500 shadow-lg'
                  : ''
              }`}
            >
            <div className="space-y-4">
              {/* Template Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="text-4xl">{template.icon}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{template.name}</h3>
                    <Badge variant={getCategoryBadgeVariant(template.category)} size="sm">
                      {template.category}
                    </Badge>
                  </div>
                </div>
                {selectedTemplate?.id === template.id && (
                  <CheckIcon className="w-6 h-6 text-primary-600 flex-shrink-0" />
                )}
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-3">{template.description}</p>

              {/* Team Info */}
              <div className="space-y-2 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Strategy:</span>
                  <span className="text-gray-900 font-medium capitalize">
                    {template.teamConfig.strategy.replace('_', ' ').toLowerCase()}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">Team Members:</span>
                  <div className="flex items-center gap-1">
                    <UsersIcon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">
                      {template.recommendedAgents.length}
                    </span>
                  </div>
                </div>
                {template.teamConfig.feedback_enabled && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Feedback Loop:</span>
                    <span className="text-green-600 font-medium">
                      ✓ {template.teamConfig.max_iterations} iterations
                    </span>
                  </div>
                )}
              </div>

              {/* Action Button */}
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  handleUseTemplate(template)
                }}
                className="w-full"
              >
                Use This Template
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Card>
          </div>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <Card className="text-center py-12">
          <p className="text-gray-600">No templates found in this category</p>
        </Card>
      )}

      {/* Template Details Panel */}
      {selectedTemplate && (
        <Card className="bg-gradient-to-br from-primary-50 to-indigo-50">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="text-3xl">{selectedTemplate.icon}</div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {selectedTemplate.name}
                  </h3>
                  <p className="text-sm text-gray-600">{selectedTemplate.description}</p>
                </div>
              </div>
              <Button variant="primary" onClick={() => handleUseTemplate(selectedTemplate)}>
                Use Template
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Recommended Agents */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                Recommended Team Members ({selectedTemplate.recommendedAgents.length})
              </h4>
              <div className="space-y-2">
                {selectedTemplate.recommendedAgents.map((agent, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-primary-500/15 rounded-full flex-shrink-0">
                      <span className="text-sm font-semibold text-primary-700">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {agent.name}
                        </span>
                        <Badge variant="gray" size="sm">
                          {agent.role}
                        </Badge>
                        {agent.isRequired && (
                          <Badge variant="error" size="sm">
                            Required
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">{agent.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
